import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import { CreditCard, ExternalLink } from "lucide-react";
import type React from "react";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useSuspenseGetSubscription } from "../../api/hooks";
import { getPlanColorClasses, getPlanFromId } from "../../upgrade/utils";

type CurrentPlanCardProps = {
  formatDate: (date: string) => string;
};

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  formatDate,
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);

  // biome-ignore lint/complexity/useSimplifiedLogicExpression: defensive guard against partial API response
  if (!subscription.customer || !subscription.plan) {
    return null;
  }

  const { customer, plan } = subscription;

  const matchingPlan = plan.external_plan_id
    ? getPlanFromId(plan.external_plan_id)
    : null;

  // Use enterprise plan as fallback if no matching plan is found
  const enterprisePlan = getPlanFromId("enterprise");
  const planToDisplay = matchingPlan || enterprisePlan;

  const displayPrice = planToDisplay?.price || "Custom";
  const displayCadence = planToDisplay?.priceDetail?.replace("/", "") || "";
  const colorClasses = getPlanColorClasses(planToDisplay?.color ?? "primary");

  return (
    <Card className="col-span-1 lg:col-span-2">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-brand-text" />
            Current Plan
          </CardTitle>
          <Badge
            className={cn(
              "capitalize",
              colorClasses.badgeText,
              colorClasses.badgeBorder
            )}
            variant="outline"
          >
            {plan.name}
          </Badge>
        </div>
        <CardDescription>
          Your current subscription plan is <strong>{plan.name}</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground text-sm">Status</span>
            <Badge
              className="capitalize"
              variant={
                subscription.status === "active" ? "default" : "secondary"
              }
            >
              {subscription.status}
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground text-sm">Price</span>
            <span className="font-medium text-sm">
              {`${displayPrice}${displayCadence ? ` / ${displayCadence}` : ""}`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground text-sm">
              Next Billing Date
            </span>
            <span className="font-medium text-sm">
              {formatDate(subscription.current_billing_period_end_date)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground text-sm">Trial Period</span>
            <span className="font-medium text-sm">
              {subscription.trial_info.end_date
                ? `Ends on ${formatDate(subscription.trial_info.end_date)}`
                : "N/A"}
            </span>
          </div>
        </div>
        <Separator />
        <Button
          className="w-full sm:w-auto"
          render={
            // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
            <a
              href={customer.portal_url}
              rel="noopener noreferrer"
              target="_blank"
            />
          }
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          View Subscription
        </Button>
      </CardContent>
    </Card>
  );
};
